import { List, Map } from 'immutable';

import { setBarcodeErrors, setCAPErrors, setCPIErrors, setPriceErrors, setRetailPriceErrors, setQuantityErrors, setSKUErrors, setWeightErrors, setWholesalePriceErrors, setWidthErrors, setLengthErrors, setHeightErrors, setGTINErrors } from '@/utils/product/errors';
import { getInventoryOperationValue } from '@/utils/product/getOperationValue';
import { getCombinationsErrors } from '@/utils/validations/variations';
import { shapeVariationsForAPI } from '@/utils/variations/shapeForAPI';
import { filterOperations } from '@/utils/product/operations';
import { formHasErrors } from '@/utils/product/formHasErrors';
import { setInvalid } from '@/utils/variations/errors';
import { getSize } from '@/utils/iterable/getSize';
import { reduce } from '@/utils/iterable/reduce';

import { CURRENCY, ERROR, OPERATIONS, VALUE } from '@/constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { COMBINATIONS, INFLUENCE } from '@/constants/variations';
import { ATTRIBUTE } from '@/constants/attributes';
import { FORM } from '@/constants/forms';

export function editInventory({ product: source, shopData, userFeatures, ...values }) {
  let product = source;
  let operations = [];
  const channel = product.get('channel');

  function getOperationType({ type }) {
    switch (channel) {
      case EBAY: {
        switch (type) {
          case ATTRIBUTE.PRICE: {
            return OPERATIONS[EBAY].PRICE;
          }

          case ATTRIBUTE.QUANTITY: {
            return OPERATIONS[EBAY].QUANTITY;
          }

          case ATTRIBUTE.SKU: {
            return OPERATIONS[EBAY].SKU;
          }

          case ATTRIBUTE.VARIATIONS: {
            return OPERATIONS[EBAY].VARIATIONS;
          }

          default: {
            return undefined;
          }
        }
      }

      case ETSY: {
        switch (type) {
          case ATTRIBUTE.PRICE: {
            return OPERATIONS[ETSY].PRICE;
          }

          case ATTRIBUTE.QUANTITY: {
            return OPERATIONS[ETSY].QUANTITY;
          }

          case ATTRIBUTE.SKU: {
            return OPERATIONS[ETSY].SKU;
          }

          default: {
            return undefined;
          }
        }
      }

      case FAIRE: {
        switch (type) {
          case ATTRIBUTE.CONTINUE_SELLING: {
            return OPERATIONS[FAIRE].CONTINUE_SELLING;
          }

          case ATTRIBUTE.DEADLINE_DATE: {
            return OPERATIONS[FAIRE].DEADLINE_DATE;
          }

          case ATTRIBUTE.DISTANCE_UNIT: {
            return OPERATIONS[FAIRE].DISTANCE_UNIT;
          }

          case ATTRIBUTE.END_DATE: {
            return OPERATIONS[FAIRE].END_DATE;
          }

          case ATTRIBUTE.GTIN: {
            return OPERATIONS[FAIRE].GTIN;
          }

          case ATTRIBUTE.HEIGHT: {
            return OPERATIONS[FAIRE].HEIGHT;
          }

          case ATTRIBUTE.KEEP_ACTIVE: {
            return OPERATIONS[FAIRE].KEEP_ACTIVE;
          }

          case ATTRIBUTE.LENGTH: {
            return OPERATIONS[FAIRE].LENGTH;
          }

          case ATTRIBUTE.MASS_UNIT: {
            return OPERATIONS[FAIRE].MASS_UNIT;
          }

          case ATTRIBUTE.PREORDER: {
            return OPERATIONS[FAIRE].PREORDER;
          }

          case ATTRIBUTE.QUANTITY: {
            return OPERATIONS[FAIRE].QUANTITY;
          }

          case ATTRIBUTE.RESTOCK_DATE: {
            return OPERATIONS[FAIRE].RESTOCK_DATE;
          }

          case ATTRIBUTE.RETAIL_PRICE:
          case ATTRIBUTE.WHOLESALE_PRICE: {
            return OPERATIONS[FAIRE].PRICES;
          }

          case ATTRIBUTE.SKU: {
            return OPERATIONS[FAIRE].SKU;
          }

          case ATTRIBUTE.START_DATE: {
            return OPERATIONS[FAIRE].START_DATE;
          }

          case ATTRIBUTE.TARIFF_CODE: {
            return OPERATIONS[FAIRE].TARIFF_CODE;
          }

          case ATTRIBUTE.WEIGHT: {
            return OPERATIONS[FAIRE].WEIGHT;
          }

          case ATTRIBUTE.VARIATIONS: {
            return OPERATIONS[FAIRE].VARIATIONS;
          }

          case ATTRIBUTE.WIDTH: {
            return OPERATIONS[FAIRE].WIDTH;
          }

          default: {
            return undefined;
          }
        }
      }

      case SHOPIFY: {
        return OPERATIONS[SHOPIFY].VARIATIONS;
      }

      default: {
        return undefined;
      }
    }
  }

  function validate({ type, ...rest }) {
    switch (channel) {
      case EBAY:
      case ETSY: {
        switch (type) {
          case ATTRIBUTE.PRICE: {
            return { product: setPriceErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.QUANTITY: {
            return { product: setQuantityErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.SKU: {
            return { product: setSKUErrors({ product: rest.product }) };
          }

          default: {
            return { product: rest.product };
          }
        }
      }

      case FAIRE: {
        switch (type) {
          case ATTRIBUTE.GTIN: {
            return {
              product: setGTINErrors({ product: rest.product }),
            };
          }

          case ATTRIBUTE.HEIGHT: {
            return {
              product: setHeightErrors({ product: rest.product }),
            };
          }

          case ATTRIBUTE.LENGTH: {
            return {
              product: setLengthErrors({ product: rest.product }),
            };
          }

          case ATTRIBUTE.RETAIL_PRICE:
          case ATTRIBUTE.WHOLESALE_PRICE: {
            return {
              product: setRetailPriceErrors({
                ...rest,
                product: setWholesalePriceErrors({ ...rest, product: rest.product }),
              }),
            };
          }

          case ATTRIBUTE.WEIGHT: {
            return {
              product: setWeightErrors({ product: rest.product }),
            };
          }

          case ATTRIBUTE.WIDTH: {
            return {
              product: setWidthErrors({ product: rest.product }),
            };
          }

          default: {
            return { product: rest.product };
          }
        }
      }

      case SHOPIFY: {
        switch (type) {
          case ATTRIBUTE.BARCODE: {
            return { product: setBarcodeErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.CAP: {
            return { product: setCAPErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.CPI: {
            return { product: setCPIErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.PHYSICAL: {
            return {
              product: setWeightErrors({ product: rest.product }),
              type: ATTRIBUTE.WEIGHT,
            };
          }

          case ATTRIBUTE.PRICE: {
            return { product: setPriceErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.QUANTITY: {
            return { product: setQuantityErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.SKU: {
            return { product: setSKUErrors({ product: rest.product }) };
          }

          case ATTRIBUTE.TRACK_QUANTITY: {
            return {
              product: setQuantityErrors({ product: rest.product }),
              type: ATTRIBUTE.QUANTITY,
            };
          }

          case ATTRIBUTE.WEIGHT: {
            return { product: setWeightErrors({ product: rest.product }) };
          }

          default: {
            return { product: rest.product };
          }
        }
      }

      default: {
        return { product: rest.product };
      }
    }
  }

  function setValuesToCombinations(combination) {
    function reduceProductValues(result, value, key) {
      return result.set(key, value);
    }

    return reduce(values, combination, reduceProductValues);
  }

  function setFairePriceValueToCombinations(type) {
    const { [type]: prices } = values;

    return function reduceCombinations(combination) {
      function reducePrices(result, price, currency) {
        return result.setIn([type, currency], price);
      }

      return reduce(prices, combination, reducePrices);
    };
  }

  function addOperation({ result, type, ...rest }) {
    switch (channel) {
      case EBAY: {
        return [
          Map({
            type: getOperationType({ type, ...rest }),
            value: getInventoryOperationValue({ channel, type, ...rest }),
          }),
        ];
      }

      case ETSY: {
        return [
          ...result,
          Map({
            type: getOperationType({ type, ...rest }),
            value: List([getInventoryOperationValue({ channel, type, ...rest })]),
          }),
        ];
      }

      case FAIRE: {
        switch (type) {
          case ATTRIBUTE.RETAIL_PRICE:
          case ATTRIBUTE.WHOLESALE_PRICE: {
            const operationType = getOperationType({ type, ...rest });
            const operationValue = getInventoryOperationValue({ channel, type, ...rest });
            return [
              ...result.filter(filterOperations(operationType, false)),
              Map({ type: operationType, value: operationValue }),
            ];
          }

          default: {
            const operationType = getOperationType({ type, ...rest });

            return operationType
              ? [
                ...result,
                Map({
                  type: getOperationType({ type, ...rest }),
                  value: List([getInventoryOperationValue({ channel, type, ...rest })]),
                }),
              ]
              : result;
          }
        }
      }

      case SHOPIFY: {
        return [
          Map({
            type: getOperationType({ type, ...rest }),
            value: getInventoryOperationValue({ channel, type, ...rest }),
          }),
        ];
      }

      default: {
        return result;
      }
    }
  }

  function setValueToInventory(result, value, type) {
    product = product.setIn([FORM.INVENTORY, type, VALUE], value);

    const validated = validate({ product, type });

    product = validated.product;

    return product.getIn([FORM.INVENTORY, validated.type || type, ERROR])
      ? result
      : addOperation({ inventory: product.get(FORM.INVENTORY), result, type, value });
  }

  function setFairePriceValueToInventory(type) {
    return function reducePrices(result, value, currency) {
      product = product.setIn([FORM.INVENTORY, type, VALUE, currency], value);

      const validated = validate({ product, type });

      product = validated.product;

      return (
        product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, ERROR]) ||
        product.getIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, ERROR])
      )
        ? result
        : addOperation({ inventory: product.get(FORM.INVENTORY), result, type });
    };
  }

  function setValueToInfluence({ attribute, value }) {
    product = product.setIn([FORM.VARIATIONS, INFLUENCE, attribute], value);
  }

  switch (channel) {
    case EBAY: {
      const combinations = product.getIn([FORM.VARIATIONS, COMBINATIONS]);

      if (getSize(combinations)) {
        product = product.setIn([FORM.VARIATIONS, COMBINATIONS], combinations.map(setValuesToCombinations));
        product = product.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: product.get(FORM.VARIATIONS) })
        );

        product = product.set(FORM.VARIATIONS, setInvalid(product.get(FORM.VARIATIONS)));

        if (!formHasErrors({ form: FORM.VARIATIONS, product, userFeatures })) {
          const operation = shapeVariationsForAPI({ product, toImmutable: true });
          operations.push(operation.set('type', getOperationType({ type: ATTRIBUTE.VARIATIONS })));
        }
      } else {
        const newOperations = reduce(values, operations, setValueToInventory);

        if (getSize(newOperations)) {
          operations = newOperations;
        }
      }

      break;
    }

    case ETSY: {
      operations = reduce(values, operations, setValueToInventory);

      const combinations = product.getIn([FORM.VARIATIONS, COMBINATIONS]);

      if (getSize(combinations)) {
        product = product.setIn([FORM.VARIATIONS, COMBINATIONS], combinations.map(setValuesToCombinations));
        product = product.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: product.get(FORM.VARIATIONS) })
        );

        product = product.set(FORM.VARIATIONS, setInvalid(product.get(FORM.VARIATIONS)));
      }

      break;
    }

    case FAIRE: {
      const {
        [ATTRIBUTE.CONTINUE_SELLING]: continueSelling,
        [ATTRIBUTE.KEEP_ACTIVE]: keepActive,
        [ATTRIBUTE.INTERNATIONAL_PRICING]: internationalPricing,
        [ATTRIBUTE.RETAIL_PRICE]: retailPrices,
        [ATTRIBUTE.WHOLESALE_PRICE]: wholesalePrices,
        ...rest
      } = values;

      if (getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS]))) {
        if (internationalPricing !== undefined) {
          operations = setValueToInventory(operations, internationalPricing, ATTRIBUTE.INTERNATIONAL_PRICING);
          setValueToInfluence({ attribute: ATTRIBUTE.INTERNATIONAL_PRICING, value: internationalPricing });
        } else if (
          !product.getIn([FORM.VARIATIONS, INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING]) && (
            (
              getSize(retailPrices) > 0 && (
                retailPrices[CURRENCY.AUD] ||
                retailPrices[CURRENCY.CAD] ||
                retailPrices[CURRENCY.EUR] ||
                retailPrices[CURRENCY.GBP]
              )
            ) || (
              getSize(wholesalePrices) > 0 && (
                wholesalePrices[CURRENCY.AUD] ||
                wholesalePrices[CURRENCY.CAD] ||
                wholesalePrices[CURRENCY.EUR] ||
                wholesalePrices[CURRENCY.GBP]
              )
            )
          )
        ) {
          operations = setValueToInventory(operations, true, ATTRIBUTE.INTERNATIONAL_PRICING);
          setValueToInfluence({ attribute: ATTRIBUTE.INTERNATIONAL_PRICING, value: true });
        }

        if (getSize(retailPrices) > 0) {
          product = product.setIn([FORM.VARIATIONS, COMBINATIONS],
            product.getIn([FORM.VARIATIONS, COMBINATIONS]).map(setFairePriceValueToCombinations(ATTRIBUTE.RETAIL_PRICE))
          );
        }

        if (getSize(wholesalePrices) > 0) {
          product = product.setIn([FORM.VARIATIONS, COMBINATIONS],
            product
              .getIn([FORM.VARIATIONS, COMBINATIONS])
              .map(setFairePriceValueToCombinations(ATTRIBUTE.WHOLESALE_PRICE))
          );
        }

        if (continueSelling !== undefined) {
          operations = setValueToInventory(operations, continueSelling, ATTRIBUTE.CONTINUE_SELLING);
          setValueToInfluence({ attribute: ATTRIBUTE.CONTINUE_SELLING, value: continueSelling });
        }

        if (keepActive !== undefined) {
          operations = setValueToInventory(operations, keepActive, ATTRIBUTE.KEEP_ACTIVE);
          setValueToInfluence({ attribute: ATTRIBUTE.KEEP_ACTIVE, value: keepActive });
        }

        if (getSize(rest) > 0) {
          product = product.setIn([FORM.VARIATIONS, COMBINATIONS],
            product.getIn([FORM.VARIATIONS, COMBINATIONS]).map(setValuesToCombinations)
          );
        }

        if (
          getSize(retailPrices) > 0 ||
          getSize(wholesalePrices) > 0 ||
          getSize(rest) > 0
        ) {
          product = product.setIn([FORM.VARIATIONS, ERROR],
            getCombinationsErrors({ channel, shopData, variations: product.get(FORM.VARIATIONS) })
          );

          product = product.set(FORM.VARIATIONS, setInvalid(product.get(FORM.VARIATIONS)));

          if (!formHasErrors({ form: FORM.VARIATIONS, product, userFeatures })) {
            const operation = shapeVariationsForAPI({ product, toImmutable: true });
            operations.push(operation.set('type', getOperationType({ type: ATTRIBUTE.VARIATIONS })));
          }
        }
      } else {
        if (internationalPricing !== undefined) {
          operations = setValueToInventory(operations, internationalPricing, ATTRIBUTE.INTERNATIONAL_PRICING);
          setValueToInfluence({ attribute: ATTRIBUTE.INTERNATIONAL_PRICING, value: internationalPricing });
        } else if (
          !product.getIn([FORM.INVENTORY, ATTRIBUTE.INTERNATIONAL_PRICING, VALUE]) && (
            (
              getSize(retailPrices) > 0 && (
                retailPrices[CURRENCY.AUD] ||
                retailPrices[CURRENCY.CAD] ||
                retailPrices[CURRENCY.EUR] ||
                retailPrices[CURRENCY.GBP]
              )
            ) || (
              getSize(wholesalePrices) > 0 && (
                wholesalePrices[CURRENCY.AUD] ||
                wholesalePrices[CURRENCY.CAD] ||
                wholesalePrices[CURRENCY.EUR] ||
                wholesalePrices[CURRENCY.GBP]
              )
            )
          )
        ) {
          operations = setValueToInventory(operations, true, ATTRIBUTE.INTERNATIONAL_PRICING);
          setValueToInfluence({ attribute: ATTRIBUTE.INTERNATIONAL_PRICING, value: true });
        }

        if (getSize(retailPrices) > 0) {
          operations = reduce(retailPrices, operations, setFairePriceValueToInventory(ATTRIBUTE.RETAIL_PRICE));
        }

        if (getSize(wholesalePrices) > 0) {
          operations = reduce(wholesalePrices, operations, setFairePriceValueToInventory(ATTRIBUTE.WHOLESALE_PRICE));
        }

        if (continueSelling !== undefined) {
          operations = setValueToInventory(operations, continueSelling, ATTRIBUTE.CONTINUE_SELLING);
          setValueToInfluence({ attribute: ATTRIBUTE.CONTINUE_SELLING, value: continueSelling });
        }

        if (keepActive !== undefined) {
          operations = setValueToInventory(operations, keepActive, ATTRIBUTE.KEEP_ACTIVE);
          setValueToInfluence({ attribute: ATTRIBUTE.KEEP_ACTIVE, value: keepActive });
        }

        if (getSize(rest) > 0) {
          operations = reduce(rest, operations, setValueToInventory);
        }
      }

      break;
    }

    case SHOPIFY: {
      const combinations = product.getIn([FORM.VARIATIONS, COMBINATIONS]);

      if (getSize(combinations)) {
        product = product.setIn([FORM.VARIATIONS, COMBINATIONS], combinations.map(setValuesToCombinations));
        product = product.setIn([FORM.VARIATIONS, ERROR],
          getCombinationsErrors({ channel, shopData, variations: product.get(FORM.VARIATIONS) })
        );

        product = product.set(FORM.VARIATIONS, setInvalid(product.get(FORM.VARIATIONS)));

        if (!formHasErrors({ form: FORM.VARIATIONS, product, userFeatures })) {
          const operation = shapeVariationsForAPI({ product, toImmutable: true });
          operations.push(operation.set('type', getOperationType({ type: ATTRIBUTE.VARIATIONS })));
        }
      } else {
        const newOperations = reduce(values, operations, setValueToInventory);

        if (getSize(newOperations)) {
          operations = newOperations;
        }
      }

      break;
    }

    default: {
      break;
    }
  }

  return { product, operations };
}
